'use client'
// =============================================================================
// RadarHero — radar mondial Amiral (public/radar_amiral.png) avec les 4 coins
// recouverts par des panneaux HUD affichant les VRAIES stats animées
// (compteurs qui défilent) : Legal Engine, réseau, ingestion, santé des villes.
// =============================================================================
import { useState, useEffect } from 'react'
import { useCountUp } from '@/hooks/useCountUp'

const ADM = '/api/legal-admin'

interface Stats {
  legalEngine?: { cities: number; documents: number; articles: number; zones: number }
  prisma?: { totalRules: number; avgRuleConfidence: number; jobsByStatus: Record<string, number> }
}
interface City {
  slug: string; name?: string; nom?: string
  docCount?: number; articleCount?: number; zoneCount?: number; ruleCount?: number
}

const GOLD = '#e8b64c'
const CYAN = '#39c4ff'

// Panneau de coin — recouvre les boîtes intégrées à l'image
function CornerPanel({ title, corner, children }: {
  title: string
  corner: 'tl' | 'tr' | 'bl' | 'br'
  children: React.ReactNode
}) {
  const pos: Record<string, React.CSSProperties> = {
    tl: { top: '2%', left: '1.5%' },
    tr: { top: '2%', right: '1.5%' },
    bl: { bottom: '2.5%', left: '1.5%' },
    br: { bottom: '2.5%', right: '1.5%' },
  }
  return (
    <div style={{
      position: 'absolute', ...pos[corner],
      width: '26%', minHeight: '17.5%',
      background: 'rgba(3, 9, 22, 0.96)',
      border: `1px solid ${GOLD}45`,
      borderRadius: 8,
      padding: 'clamp(6px, 1.2vw, 12px)',
      display: 'flex', flexDirection: 'column', gap: 'clamp(2px, 0.5vw, 5px)',
      boxShadow: '0 0 20px rgba(0,0,0,0.6)',
      overflow: 'hidden',
    }}>
      <div style={{
        fontSize: 'clamp(7px, 0.9vw, 10px)', fontWeight: 800, color: GOLD,
        fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.12em',
        borderBottom: `1px solid ${GOLD}25`, paddingBottom: 'clamp(2px, 0.4vw, 4px)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {title}
      </div>
      {children}
    </div>
  )
}

function StatRow({ label, value, color = CYAN, suffix = '' }: {
  label: string; value: number; color?: string; suffix?: string
}) {
  const shown = useCountUp(value, 1600)
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, fontFamily: 'var(--font-mono, monospace)' }}>
      <span style={{ fontSize: 'clamp(6px, 0.8vw, 9px)', color: 'rgba(148,163,184,0.85)', letterSpacing: '0.06em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </span>
      <span style={{ fontSize: 'clamp(8px, 1.1vw, 13px)', fontWeight: 800, color, whiteSpace: 'nowrap' }}>
        {shown.toLocaleString('fr-CA')}{suffix}
      </span>
    </div>
  )
}

function CityRow({ city }: { city: City }) {
  const pillars = [city.docCount, city.articleCount, city.zoneCount, city.ruleCount].map(v => (v ?? 0) > 0)
  const score = Math.round(pillars.filter(Boolean).length / 4 * 100)
  const color = score >= 75 ? '#34d399' : score >= 50 ? '#f5a623' : score > 0 ? '#fb7185' : '#475569'
  const shown = useCountUp(score, 1600)
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, fontFamily: 'var(--font-mono, monospace)' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        <span style={{ width: 4, height: 4, borderRadius: '50%', background: color, flexShrink: 0, boxShadow: `0 0 4px ${color}` }} />
        <span style={{ fontSize: 'clamp(6px, 0.8vw, 9px)', color: 'rgba(148,163,184,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {city.name ?? city.nom ?? city.slug}
        </span>
      </span>
      <span style={{ fontSize: 'clamp(8px, 1.1vw, 13px)', fontWeight: 800, color, whiteSpace: 'nowrap' }}>{shown}%</span>
    </div>
  )
}

export function RadarHero() {
  const [stats, setStats]   = useState<Stats | null>(null)
  const [cities, setCities] = useState<City[]>([])

  useEffect(() => {
    fetch(`${ADM}/stats`).then(r => r.ok ? r.json() : null).then(setStats).catch(() => {})
    fetch(`${ADM}/cities?page=1&limit=100`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setCities(Array.isArray(d?.items) ? d.items : []))
      .catch(() => {})
  }, [])

  const le = stats?.legalEngine
  const pr = stats?.prisma
  const jobs = pr?.jobsByStatus ?? {}
  const jobsDone  = (jobs['done'] ?? 0) + (jobs['completed'] ?? 0)
  const jobsError = (jobs['error'] ?? 0) + (jobs['failed'] ?? 0)
  const jobsRun   = (jobs['running'] ?? 0) + (jobs['pending'] ?? 0)
  const confiance = Math.round((pr?.avgRuleConfidence ?? 0) * 100)

  return (
    <div style={{
      position: 'relative',
      maxWidth: 'min(720px, 100%)',
      aspectRatio: '1328 / 1184',
      margin: '20px 0 18px',
      borderRadius: 16,
      overflow: 'hidden',
      border: '1px solid rgba(232,182,76,0.22)',
      boxShadow: '0 0 40px rgba(8,20,45,0.8), inset 0 0 60px rgba(0,0,0,0.3)',
    }}>
      <img
        src="/radar_amiral.png"
        alt="Amiral Global Radar"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />

      {/* ── Logo Amiral Blanc — centré entre les 2 panneaux du haut,
             légèrement descendu pour laisser le N (nord) visible ── */}
      <div style={{
        position: 'absolute', top: '6%', left: '50%', transform: 'translateX(-50%)',
        width: 'clamp(64px, 16%, 130px)', aspectRatio: '820 / 921',
        display: 'grid', placeItems: 'center', pointerEvents: 'none', zIndex: 2,
      }}>
        <div style={{
          position: 'absolute', inset: '-28%', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(79,124,255,0.22) 0%, rgba(0,212,255,0.08) 50%, transparent 70%)',
          animation: 'hudPulse 3.5s ease-in-out infinite',
        }} />
        <img
          src="/logo.png"
          alt="Amiral Blanc — Fier d'ici"
          style={{
            width: '100%', height: '100%', objectFit: 'contain', position: 'relative',
            filter: 'drop-shadow(0 0 28px rgba(79,124,255,0.55)) drop-shadow(0 0 10px rgba(0,212,255,0.3))',
            animation: 'hudPulse 4.5s ease-in-out infinite',
          }}
        />
      </div>

      {/* ── Coin haut-gauche : Legal Engine ── */}
      <CornerPanel title="⚖ LEGAL ENGINE" corner="tl">
        <StatRow label="VILLES"    value={le?.cities    ?? 0} color={CYAN} />
        <StatRow label="DOCUMENTS" value={le?.documents ?? 0} color={CYAN} />
        <StatRow label="ARTICLES"  value={le?.articles  ?? 0} color={GOLD} />
      </CornerPanel>

      {/* ── Coin haut-droit : réseau / structuration ── */}
      <CornerPanel title="AMIRAL NETWORK" corner="tr">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: 'var(--font-mono, monospace)' }}>
          <span style={{ fontSize: 'clamp(6px, 0.8vw, 9px)', color: 'rgba(148,163,184,0.85)', letterSpacing: '0.06em' }}>SYSTÈME</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#34d399', boxShadow: '0 0 6px #34d399', animation: 'hudPulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 'clamp(7px, 0.95vw, 11px)', fontWeight: 800, color: '#34d399' }}>ACTIF</span>
          </span>
        </div>
        <StatRow label="ZONES"  value={le?.zones      ?? 0} color={CYAN} />
        <StatRow label="RÈGLES" value={pr?.totalRules ?? 0} color={GOLD} />
      </CornerPanel>

      {/* ── Coin bas-gauche : ingestion ── */}
      <CornerPanel title="⚙ INGESTION" corner="bl">
        <StatRow label="RÉUSSIS"  value={jobsDone}  color="#34d399" />
        <StatRow label="ERREURS"  value={jobsError} color="#ef4444" />
        {jobsRun > 0
          ? <StatRow label="EN COURS" value={jobsRun} color="#f5a623" />
          : <StatRow label="CONFIANCE" value={confiance} color="#f472b6" suffix="%" />}
      </CornerPanel>

      {/* ── Coin bas-droit : santé des villes ── */}
      <CornerPanel title="🏛 VILLES ANALYSÉES" corner="br">
        {cities.slice(0, 3).map(c => <CityRow key={c.slug} city={c} />)}
        {cities.length === 0 && (
          <span style={{ fontSize: 'clamp(6px, 0.8vw, 9px)', color: 'rgba(148,163,184,0.6)', fontFamily: 'var(--font-mono, monospace)' }}>
            En attente d'analyse…
          </span>
        )}
        {cities.length > 3 && (
          <span style={{ fontSize: 'clamp(6px, 0.75vw, 8px)', color: 'rgba(148,163,184,0.5)', fontFamily: 'var(--font-mono, monospace)' }}>
            +{cities.length - 3} autres
          </span>
        )}
      </CornerPanel>

      {/* Légende bas centre */}
      <div style={{
        position: 'absolute', bottom: '0.8%', left: '50%', transform: 'translateX(-50%)',
        fontSize: 'clamp(6px, 0.8vw, 9px)', color: `${GOLD}cc`,
        fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.3em',
        background: 'rgba(3,9,22,0.85)', padding: '2px 12px', borderRadius: 6,
        whiteSpace: 'nowrap',
      }}>
        AMIRAL GLOBAL RADAR°
      </div>
    </div>
  )
}
